/**
 * Die angemeldeten Geräte (HM-101, ADR 0029): laden, eine beenden, alle
 * anderen beenden.
 *
 * Eine Sitzung ist eine Kette von Auffrischungstoken — je Anmeldung eine. Was
 * die Liste zeigt, sagt der Server; hier steht nur der Weg dorthin und die
 * Beschriftung eines Geräts.
 */
import { useQuery } from "@tanstack/react-query";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "./client.js";

export type Session = ApiComponents["schemas"]["Session"];
export type SessionClient = ApiComponents["schemas"]["SessionClient"];

export const SESSIONS_QUERY_KEY = ["auth", "sessions"] as const;

export function useSessions() {
  return useQuery({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await api.GET("/auth/sessions", {});
      if (error || !data) throw new Error("Die angemeldeten Geräte konnten nicht geladen werden.");
      return data;
    },
  });
}

export async function revokeSession(sessionId: string): Promise<void> {
  const { error } = await api.DELETE("/auth/sessions/{sessionId}", {
    params: { path: { sessionId } },
  });
  if (error) throw new Error("Die Sitzung konnte nicht beendet werden.");
}

export async function revokeOtherSessions(): Promise<void> {
  const { error } = await api.DELETE("/auth/sessions/others", {});
  if (error) throw new Error("Die anderen Sitzungen konnten nicht beendet werden.");
}

/**
 * „Safari auf iPhone", „Firefox", „Windows" — oder `null`, wenn der Server
 * nichts erkannt hat. **Kein Platzhalter wie „Unbekanntes Gerät":** Was die
 * Oberfläche daraus macht, entscheidet sie an der Stelle, an der der Satz
 * steht; ein Gerät, das „unbekannt" heißt, sähe aus wie ein fremdes.
 */
export function sessionClientLabel(client: SessionClient | null): string | null {
  if (client === null) return null;
  if (client.browser !== null && client.os !== null) return `${client.browser} auf ${client.os}`;
  return client.browser ?? client.os;
}
